/* import __COLOCATED_TEMPLATE__ from './visibility.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { MacroVisibility } from 'embercom/objects/inbox/macro';

export default class Visibility extends Component {
  @service appService;
  @service intl;
  @tracked teamPaywall;

  get app() {
    return this.appService.app;
  }

  get visibility() {
    return this.args.visibility;
  }

  get showTeamSelect() {
    return this.visibility === MacroVisibility.Teams && this.app.canSetMacroVisibility;
  }

  get noPermissions() {
    let permissions = this.app.currentAdmin.currentAppPermissions;
    if (this.args.hasCreateEditMacrosFF) {
      // With FF ON, disable selector only if user has neither shared nor personal permission
      return !this.args.canCreateSharedMacros && !permissions.can_use_personal_macros;
    }
    // FF OFF: disable if no manage saved replies permission
    return !permissions.can_manage_saved_replies;
  }

  shouldDisableOption(visibility) {
    // In read-only/edit mode, don't disable individual options (whole selector is disabled)
    if (this.args.isDisabled) {
      return false;
    }
    if (visibility === MacroVisibility.Myself) {
      return !this.app.currentAdmin.currentAppPermissions.can_use_personal_macros;
    }
    return !this.args.canCreateSharedMacros;
  }

  getDisabledTooltipText(visibility) {
    if (
      visibility === MacroVisibility.Myself &&
      !this.app.currentAdmin.currentAppPermissions.can_use_personal_macros
    ) {
      return this.intl.t('inbox.create-macro.disabled-tooltip.personal');
    } else if (visibility !== MacroVisibility.Myself && !this.args.canCreateSharedMacros) {
      return this.intl.t('inbox.create-macro.disabled-tooltip.shared');
    }
    return undefined;
  }

  get selectItems() {
    // FF OFF: Use master behavior - always show all 3 options with Myself disabled if no permission
    if (!this.args.hasCreateEditMacrosFF) {
      return this._masterSelectItems();
    }

    // FF ON: Show all options, disable based on permissions (matching inbox2 behavior)
    let items = [
      {
        value: MacroVisibility.Everyone,
        text: this.intl.t('inbox.manage-macros.visibility.everyone'),
        isSelected: this.visibility === MacroVisibility.Everyone,
        isDisabled: this.shouldDisableOption(MacroVisibility.Everyone),
        tooltipText: this.getDisabledTooltipText(MacroVisibility.Everyone),
      },
    ];

    let teamItem = {
      value: MacroVisibility.Teams,
      text: this.intl.t('inbox.manage-macros.visibility.specific-teams'),
      isSelected: this.visibility === MacroVisibility.Teams,
      isDisabled: this.shouldDisableOption(MacroVisibility.Teams),
      tooltipText: this.getDisabledTooltipText(MacroVisibility.Teams),
    };

    if (this.teamPaywall?.isActive) {
      teamItem = {
        ...teamItem,
        component: 'paywalls/select-item',
        componentShouldReplaceItem: false,
        paywallOpenUpgradeModal: this.teamPaywall?.openUpgradeModal,
        paywallAnalyticsEventData: this.teamPaywall?.analyticsEventData,
      };
    }

    items.push(teamItem);

    items.push({
      value: MacroVisibility.Myself,
      text: this.intl.t('inbox.manage-macros.visibility.myself-only'),
      isSelected: this.visibility === MacroVisibility.Myself,
      isDisabled: this.shouldDisableOption(MacroVisibility.Myself),
      tooltipText: this.getDisabledTooltipText(MacroVisibility.Myself),
      componentShouldReplaceItem: false,
      component: 'settings/saved-replies/owner-select-item',
    });

    return items;
  }

  // Master behavior: always show all 3 options, with Myself disabled if no permission
  _masterSelectItems() {
    let items = [
      {
        value: MacroVisibility.Everyone,
        text: this.intl.t('inbox.manage-macros.visibility.everyone'),
        isSelected: this.visibility === MacroVisibility.Everyone,
      },
    ];

    let teamItem = {
      value: MacroVisibility.Teams,
      text: this.intl.t('inbox.manage-macros.visibility.specific-teams'),
      isSelected: this.visibility === MacroVisibility.Teams,
    };

    if (this.teamPaywall?.isActive) {
      teamItem = {
        ...teamItem,
        component: 'paywalls/select-item',
        componentShouldReplaceItem: false,
        paywallOpenUpgradeModal: this.teamPaywall?.openUpgradeModal,
        paywallAnalyticsEventData: this.teamPaywall?.analyticsEventData,
      };
    }

    items.push(teamItem);
    items.push({
      value: MacroVisibility.Myself,
      text: this.intl.t('inbox.manage-macros.visibility.myself-only'),
      isSelected: this.visibility === MacroVisibility.Myself,
      isDisabled: !this.app.currentAdmin.currentAppPermissions.can_use_personal_macros,
      componentShouldReplaceItem: false,
      component: 'settings/saved-replies/owner-select-item',
    });

    return items;
  }

  @action
  onChange(newVisibility) {
    if (newVisibility === MacroVisibility.Teams) {
      this.args.onVisibilityChange([], newVisibility);
    } else {
      this.args.onVisibilityChange(null, newVisibility);
    }
  }

  @action
  selectedTeamsChanged(newTeams) {
    this.args.onVisibilityChange(newTeams, this.visibility);
  }
}
